import { memo, useCallback } from 'react';
import { Dimensions, Pressable } from 'react-native';
import { useTranslation } from '@betty/i18n';
import { BottomSheet } from '@betty/navigation';
import { formatAmount } from '@betty/toolkit/utils';
import { Box, Text, Icon, Image, PressableIcon } from '@betty/moonlight';
import { PromoProps } from './Promo.types';
import { PROMO_IMAGES } from './Promo.content';
import PromoContainer from '../PromoContainer/PromoContainer';
import PromoRewards from '../PromoRewards/PromoRewards';

function Promo({
  label,
  locked,
  rewards,
  selected,
  promotionId,
  subTypeMeta,
  miniImageUrl,
  rewardDescription,
  termsAndConditions,
  onPress,
}: PromoProps) {
  const { t } = useTranslation('wallet');
  const {
    percentage,
    maximumRewardAmount = 0,
    minimumQualificationAmount = 0,
  } = subTypeMeta || {};
  const { rewardTarget } = rewards[0];
  const multiRewards = rewards.length > 1;

  const handlePress = useCallback(() => {
    onPress?.(promotionId);
  }, [promotionId, onPress]);

  const handleInfoPress = useCallback(() => {
    BottomSheet.open({
      namespace: 'wallet',
      type: 'DepositPromoInfo',
      maxDynamicContentSize: Dimensions.get('window').height * 0.8,
      scrollable: true,
      data: {
        info: termsAndConditions,
      },
    });
  }, [termsAndConditions]);

  const handleZeroBonusPlaythroughsPress = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'Info',
      data: {
        textNS: 'wallet',
        textKey: 'depositNooWagering',
      },
    });
  }, []);

  return (
    <PromoContainer locked={locked} selected={selected} onPress={handlePress}>
      <Box centerContent>
        <Image
          name={PROMO_IMAGES[rewardTarget]}
          source={miniImageUrl}
          borderRadius="radius-200"
          width={80}
          height={80}
        />
        {locked && (
          <>
            <Box
              position="absolute"
              backgroundColor="primaryBackground"
              opacity={0.4}
              inset={0}
            />
            <Image
              name="wallet.lockedPromo"
              position="absolute"
              width={86}
              height={48}
            />
          </>
        )}
        <PressableIcon
          name="info"
          color="iconSecondary"
          position="absolute"
          right="space-100"
          bottom="space-100"
          width="space-500"
          height="space-500"
          borderRadius="radius-full"
          backgroundColor="primaryBackgroundHover"
          onPress={handleInfoPress}
        />
      </Box>
      <Box flex={1} gap="space-200">
        <Box gap="space-100">
          {locked && (
            <Text variant="body-s" color="darkSurfaceMuted">
              {t('depositToUnlock', {
                amount: formatAmount(minimumQualificationAmount),
              })}
            </Text>
          )}
          {percentage !== undefined && (
            <Text variant="body-s" color="darkSurfacePrimary">
              {t('depositPercentReward', {
                percentage,
                maximumRewardAmount: formatAmount(maximumRewardAmount),
                rewardTarget,
              })}
            </Text>
          )}
          {percentage && locked ? null : (
            <Text variant="body-s-semi" color="darkSurfacePrimary">
              {multiRewards ? rewardDescription : label}
            </Text>
          )}
          {multiRewards || !rewardDescription ? null : (
            <Text variant="body-s" color="darkSurfacePrimary">
              {rewardDescription}
            </Text>
          )}
          {rewards.length > 1 && <PromoRewards rewards={rewards} />}
          {locked ? null : (
            <Pressable
              style={{ alignSelf: 'flex-start' }}
              onPress={handleZeroBonusPlaythroughsPress}
            >
              <Box
                variant="gradient"
                alignSelf="flex-start"
                borderRadius="radius-full"
                paddingVertical="space-100"
                paddingHorizontal="space-200"
                marginTop="space-200"
                flexDirection="row"
                alignItems="center"
                gap="space-100"
              >
                <Icon
                  name="info"
                  color="iconPrimary"
                  width="space-400"
                  height="space-400"
                />
                <Text
                  variant="caption-semi"
                  locNS="wallet"
                  locKey="depositZeroBonusPlaythroughs"
                  color="accentSurfacePrimary"
                />
              </Box>
            </Pressable>
          )}
        </Box>
      </Box>
    </PromoContainer>
  );
}

export default memo(Promo);
